// FIXME: to be renamed
import type { JSONValue } from '@ai-sdk/provider';
import { generateId } from '@ai-sdk/provider-utils';
import { useStore } from '@nanostores/react';
import { useCallback, useEffect, useRef } from 'react';
import { getGithubToken } from './github-api';
import { userStore } from './stores/user';
import { GITHUB_BRANCH_PARAM, GITHUB_SYNC_PREFIX } from '~/lib/constants';
import type { Message } from '~/types/chat';
import type { RepoFullName } from '~/types/github';
import { stripIndents } from '~/utils/stripIndent';

const GITHUB_ANNOTATION_KEY = 'ghb';
const GITHUB_IMPORT_INTEGRATION = '_github_import_integration_';

export function navigateToImport(repo: RepoFullName) {
  const next = new URL(`/github.com/${repo.owner}/${repo.name}`, window.origin);

  sessionStorage.setItem(GITHUB_IMPORT_INTEGRATION, '1');

  window.location.assign(next);
}

export interface GetGithubToken {
  (): Promise<string | undefined>;
}

export function useGithubToken() {
  const featureFlags = useStore(userStore.featureFlags);

  const featureFlagsRef = useRef(featureFlags);

  useEffect(() => {
    featureFlagsRef.current = featureFlags;
  });

  return useCallback(() => {
    return getGithubToken({ orgs: featureFlagsRef.current.githubOrgs });
  }, []);
}

export function requiresIntegration() {
  return sessionStorage.getItem(GITHUB_IMPORT_INTEGRATION) != null;
}

export function resetRequiresIntegration() {
  sessionStorage.removeItem(GITHUB_IMPORT_INTEGRATION);
}

export function navigateToBranch(branch: string) {
  const next = new URL(window.location.href);

  next.searchParams.set(GITHUB_BRANCH_PARAM, branch);

  window.location.assign(next);
}

export function resetExplicitBranch() {
  const url = new URL(window.location.href);

  url.searchParams.delete(GITHUB_BRANCH_PARAM);

  window.history.replaceState(undefined, '', url.toString());
}

export function githubBranchSwitchMessage(branchName: string): Message | undefined {
  const content = `You are now working on branch \`${branchName}\``;

  return {
    id: generateId(),
    type: 'chat',
    role: 'assistant',
    status: 'complete',
    content,
    annotations: [{ bolt: true, [GITHUB_ANNOTATION_KEY]: branchName }],
    merge: false,
    parts: [{ type: 'text', text: content }],
  };
}

export function isGithubBranchSwitch(message: Message) {
  return githubBranchSwitch(message) != null;
}

export function githubBranchSwitch(message: Message) {
  if (message.role !== 'assistant' || !message.annotations) {
    return undefined;
  }

  const annotation = message.annotations.find(isGithubSwitchAnnotation);

  if (!annotation) {
    return undefined;
  }

  return (annotation as { [GITHUB_ANNOTATION_KEY]: string })[GITHUB_ANNOTATION_KEY];
}

function isGithubSwitchAnnotation(annotation: JSONValue) {
  return (
    annotation &&
    typeof annotation === 'object' &&
    !Array.isArray(annotation) &&
    annotation.bolt &&
    annotation[GITHUB_ANNOTATION_KEY]
  );
}

export function githubSyncMessage(oid: string): Message {
  const artifact = stripIndents`
    <boltArtifact id="github-sync" title="Update codebase with external changes">
      <boltAction type="github-sync" oid="${oid}"></boltAction>
    </boltArtifact>
  `;

  return {
    id: `${GITHUB_SYNC_PREFIX}${oid}`,
    type: 'chat',
    role: 'assistant',
    status: 'complete',
    content: `I’ve detected changes that were made externally. Let’s update our codebase here to reflect those.\n\n${artifact}`,
    ignore: true,
    merge: false,
    parts: [],
  };
}

export function updateGitignore(contents: string) {
  const hasEnv = contents.match(/^ *\.env *(#.*)?$/m);

  if (hasEnv) {
    return contents;
  }

  return `${contents.replace(/\n$/, '')}\n.env\n`;
}

/**
 * The "name" in UserData is not necessarily the github username.
 */
export function userData<T extends { name?: string }>(user: T | undefined, username: string) {
  return user?.name === username ? user : { ...user, name: username };
}
